// Settings → Pick-up range (0301), with the last week's evidence beside it.
//
// The number decides how close a therapist must be to her rider before Confirm
// Pick-Up goes through — both legs. 50 m is the approved value (0248 / R8, D1
// 2026-10-07). The stats underneath are why this card exists next to the
// number: before 0293 a refused slide left no trace, so the range was argued
// about without data.

import { Button } from "@bednbody/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@bednbody/ui/components/card";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { type OperatingSettings, useUpdateOperatingSettings } from "./settings.hooks";
import { Field, NumberInput } from "./settingsFields";
import { PICKUP_STATS_DAYS, usePickupStats } from "./pickupStats";

const APPROVED_RANGE_M = 50;

export function PickupRangeCard({ settings }: { settings: OperatingSettings }) {
  const update = useUpdateOperatingSettings();
  const stats = usePickupStats();
  const [draft, setDraft] = useState(() => String(settings.pickup_range_m));

  useEffect(() => {
    setDraft(String(settings.pickup_range_m));
  }, [settings]);

  const parsed = Number(draft);
  // The database refuses outside 10–500 (0301); checked here so the reason is
  // readable before the round trip.
  const valid = Number.isInteger(parsed) && parsed >= 10 && parsed <= 500;

  function save() {
    if (!valid) {
      toast.error("The pick-up range has to be a whole number of metres, 10 to 500.");
      return;
    }
    update.mutate(
      { pickup_range_m: parsed },
      {
        onSuccess: () => toast.success("Pick-up range saved."),
        onError: (error) => toast.error(error.message),
      },
    );
  }

  const s = stats.data;

  return (
    <Card className="gap-5">
      <CardHeader className="gap-2">
        <CardTitle className="type-title-body"><h2>Pick-up range</h2></CardTitle>
        <CardDescription className="max-w-prose leading-relaxed">
          How close a therapist must be to her rider before Confirm Pick-Up goes through, on the
          first pick-up and on a Return. Both phones must also have sent a location in the last 15
          seconds.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex min-w-0 flex-col gap-5">
        <Field label="Range (metres, 10–500)">
          <NumberInput value={draft} onChange={setDraft} />
        </Field>
        {parsed !== APPROVED_RANGE_M && valid ? (
          <p className="max-w-prose text-sm leading-relaxed text-warning">
            {APPROVED_RANGE_M} m is the approved range. {parsed} m is a deliberate exception.
          </p>
        ) : null}

        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">Last {PICKUP_STATS_DAYS} days</p>
          {stats.isLoading ? (
            <p className="max-w-prose text-sm leading-relaxed text-muted-foreground">Loading…</p>
          ) : stats.isError || !s ? (
            <p className="max-w-prose text-sm leading-relaxed text-muted-foreground">
              Couldn&apos;t load the pick-up history. That is a failed read, not an empty week.
            </p>
          ) : (
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>
                {s.confirmed} confirmed
                {s.medianConfirmedM !== null
                  ? ` · typical distance ${Math.round(s.medianConfirmedM)} m`
                  : ""}
              </li>
              <li>
                {s.refused} refused — {s.refusedByCode.RIDER_TOO_FAR} too far,{" "}
                {s.refusedByCode.THERAPIST_LOCATION_STALE} her location too old,{" "}
                {s.refusedByCode.RIDER_LOCATION_STALE} rider&apos;s location too old
              </li>
              <li>{s.overridden} forced by a Super Admin</li>
            </ul>
          )}
        </div>
      </CardContent>
      <CardFooter className="justify-end">
        <Button onClick={save} disabled={update.isPending || !valid}>
          Save changes
        </Button>
      </CardFooter>
    </Card>
  );
}
